import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toastSuccess, toastError } from '../ui/toast';
import { logAuditAction, AUDIT_ACTIONS, RESOURCE_TYPES } from '../utils/auditLog';
import { colors as theme } from '../theme';

const DeletionRequests = ({ onBack, onCountChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null); // request id being actioned

  useEffect(() => { loadRequests(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('deletion_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
      const pending = (data || []).filter(r => r.status === 'pending').length;
      onCountChange?.(pending);
    } catch (err) {
      console.error('Error loading deletion requests:', err);
      toastError('Could not load deletion requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (request, newStatus) => {
    setActioning(request.id);
    try {
      const { error } = await supabase
        .from('deletion_requests')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', request.id);

      if (error) throw error;

      logAuditAction(
        newStatus === 'approved' ? AUDIT_ACTIONS.APPROVE_DELETION_REQUEST : AUDIT_ACTIONS.DISMISS_DELETION_REQUEST,
        RESOURCE_TYPES.DELETION_REQUEST,
        request.id,
        { status: request.status },
        { status: newStatus, userName: request.user_name, userEmail: request.user_email }
      );

      toastSuccess(newStatus === 'approved' ? 'Request approved — remember to delete the user account.' : 'Request dismissed.');
      await loadRequests();
    } catch (err) {
      console.error('Error updating deletion request:', err);
      toastError('Could not update request');
    } finally {
      setActioning(null);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending:  { bg: 'rgba(255,149,0,0.15)',   border: 'rgba(255,149,0,0.4)',   color: theme.warning, label: 'Pending' },
      approved: { bg: 'rgba(255,59,48,0.12)',   border: 'rgba(255,59,48,0.35)',  color: theme.danger, label: 'Approved' },
      dismissed:{ bg: 'rgba(142,142,147,0.12)', border: 'rgba(142,142,147,0.3)', color: theme.textMuted, label: 'Dismissed' },
    };
    const s = styles[status] || styles.dismissed;
    return (
      <span style={{
        padding: '3px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 600,
        backgroundColor: s.bg, border: `1px solid ${s.border}`, color: s.color,
      }}>
        {s.label}
      </span>
    );
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const pending = requests.filter(r => r.status === 'pending');
  const resolved = requests.filter(r => r.status !== 'pending');

  return (
    <div style={{ padding: '24px 20px', backgroundColor: theme.bg, minHeight: '100vh', maxWidth: '700px', margin: '0 auto', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent', border: `1px solid ${theme.gold}`,
            color: theme.gold, padding: '10px 18px', borderRadius: '8px',
            cursor: 'pointer', fontSize: '13px', fontWeight: 600,
          }}
        >
          ← Back
        </button>
        <div>
          <h1 style={{ color: theme.gold, margin: 0, fontSize: '20px', fontWeight: 700 }}>Deletion Requests</h1>
          <p style={{ color: theme.textMuted, margin: '4px 0 0', fontSize: '13px' }}>
            Review and action account deletion requests from clients
          </p>
        </div>
      </div>

      {loading ? (
        <p style={{ color: theme.textMuted }}>Loading...</p>
      ) : requests.length === 0 ? (
        <div style={{
          backgroundColor: theme.surface1, borderRadius: '14px', padding: '32px',
          textAlign: 'center', border: `1px solid ${theme.surface3}`,
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✅</div>
          <div style={{ color: theme.text, fontWeight: 600, marginBottom: '6px' }}>No deletion requests</div>
          <div style={{ color: theme.textMuted, fontSize: '13px' }}>All clear — no clients have requested account deletion.</div>
        </div>
      ) : (
        <>
          {/* Pending */}
          {pending.length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: theme.warning, fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 12px' }}>
                Pending ({pending.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pending.map(req => (
                  <RequestCard
                    key={req.id}
                    req={req}
                    actioning={actioning}
                    onAction={handleAction}
                    statusBadge={statusBadge}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Resolved */}
          {resolved.length > 0 && (
            <div>
              <h3 style={{ color: theme.textMuted, fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 12px' }}>
                Resolved ({resolved.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {resolved.map(req => (
                  <RequestCard
                    key={req.id}
                    req={req}
                    actioning={actioning}
                    onAction={handleAction}
                    statusBadge={statusBadge}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RequestCard = ({ req, actioning, onAction, statusBadge, formatDate }) => (
  <div style={{
    backgroundColor: theme.surface1, borderRadius: '14px', padding: '18px 20px',
    border: req.status === 'pending' ? '1px solid rgba(255,149,0,0.3)' : `1px solid ${theme.surface3}`,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
      <div>
        <div style={{ color: theme.text, fontWeight: 600, fontSize: '15px' }}>
          {req.user_name || 'Unknown user'}
        </div>
        <div style={{ color: theme.textMuted, fontSize: '13px', marginTop: '2px' }}>
          {req.user_email || '—'}
        </div>
      </div>
      {statusBadge(req.status)}
    </div>

    <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: theme.textMuted, marginBottom: req.status === 'pending' ? '14px' : 0 }}>
      <span>Requested: {formatDate(req.requested_at)}</span>
      {req.reviewed_at && <span>Reviewed: {formatDate(req.reviewed_at)}</span>}
    </div>

    {req.status === 'pending' && (
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => onAction(req, 'approved')}
          disabled={actioning === req.id}
          style={{
            padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
            border: `1px solid ${theme.danger}`, backgroundColor: 'transparent', color: theme.danger,
            cursor: actioning === req.id ? 'not-allowed' : 'pointer',
            opacity: actioning === req.id ? 0.6 : 1,
          }}
        >
          {actioning === req.id ? 'Saving…' : 'Approve & delete account'}
        </button>
        <button
          onClick={() => onAction(req, 'dismissed')}
          disabled={actioning === req.id}
          style={{
            padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
            border: `1px solid ${theme.surface3}`, backgroundColor: 'transparent', color: theme.textMuted,
            cursor: actioning === req.id ? 'not-allowed' : 'pointer',
            opacity: actioning === req.id ? 0.6 : 1,
          }}
        >
          Dismiss
        </button>
      </div>
    )}
  </div>
);

export default DeletionRequests;
